import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../../../utils/exportColors';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { jsPDF } from 'jspdf';
import {
    baselineOffset,
    exportBaselineShift,
    EXPORT_ABSOLUTE_DRIFT_PT,
    VALUE_LINE_BOX,
} from '../shared/formFieldEmphasis';
import {
    ensureKalpurushFontLoaded,
    FAMILY_CARD_FONT_FAMILY,
} from '../family-card-form/familyCardFont';

const EXPORT_SCALE = 2;

/**
 * The same print corrections as the family card (see generateFamilyCardPdf):
 * on paper the dotted line drops a shade and the answers rise a shade, so a
 * descender never brushes the leader.
 */
const EXPORT_DOT_LINE_DROP = 1.5;
const EXPORT_VALUE_EXTRA_LIFT = 0.5;
/** Room under a clipped answer, so html2canvas's lower baseline keeps descenders. */
const EXPORT_DESCENDER_ROOM = 8;

// The cloned document holds both the live preview and the off-screen copy this
// export renders, so every page node has to be prepared.
function prepareFuelCardClone(doc) {
    doc.querySelectorAll('[data-fuel-card-page]').forEach((root) => {
        // html2canvas can't parse oklch/lab/color() — rewrite any to rgb first.
        sanitizeExportColors(root);
        root.style.overflow = 'visible';
        root.style.transform = 'none';
        // The dotted line drops on the page; an answer rides inside it, so it is
        // pulled back up by the same amount before its own extra lift.
        root.querySelectorAll('[data-field-line]').forEach((el) => {
            el.style.position = 'relative';
            el.style.top = `${EXPORT_DOT_LINE_DROP}px`;
        });
        // An answer is placed by baseline above its dotted line: shrinking its
        // line box to the baseline offset lands html2canvas's baseline where CSS
        // puts it, and the explicit height keeps room for descenders.
        root.querySelectorAll('[data-field-value]').forEach((el) => {
            const size = parseFloat(el.style.fontSize) || 0;
            if (!size) return;
            const lift = EXPORT_ABSOLUTE_DRIFT_PT + EXPORT_DOT_LINE_DROP + EXPORT_VALUE_EXTRA_LIFT;
            el.style.paddingBottom = '0';
            el.style.verticalAlign = 'baseline';
            el.style.lineHeight = `${baselineOffset(size, VALUE_LINE_BOX * size)}px`;
            el.style.height = `${VALUE_LINE_BOX * size + EXPORT_DESCENDER_ROOM}px`;
            el.style.top = `${parseFloat(el.style.top) - lift}px`;
            el.style.overflow = 'hidden';
            el.style.textOverflow = 'ellipsis';
        });
        // Text in normal flow: nudge the paint, not the layout, so the dotted
        // lines stay where the preview has them.
        const view = root.ownerDocument.defaultView;
        root.querySelectorAll('*').forEach((el) => {
            if (el.hasAttribute('data-field-value')) return;
            const hasText = Array.from(el.childNodes)
                .some((node) => node.nodeType === 3 && node.textContent.trim());
            if (!hasText) return;
            const style = view.getComputedStyle(el);
            if (style.position !== 'static') return;
            const size = parseFloat(style.fontSize) || 0;
            const lineHeight = parseFloat(style.lineHeight) || 0;
            if (!size || !lineHeight) return;
            el.style.position = 'relative';
            el.style.top = `${exportBaselineShift(size, lineHeight)}px`;
        });
        root.querySelectorAll('[data-title-gap]').forEach((el) => {
            el.style.display = 'block';
            el.style.height = '24px';
            el.style.minHeight = '24px';
        });
        root.style.fontFamily = FAMILY_CARD_FONT_FAMILY;
    });
}

function cloneForExport(element) {
    const clone = element.cloneNode(true);
    const width = element.scrollWidth;
    const height = element.scrollHeight;

    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = [
        'position:fixed',
        'left:-20000px',
        'top:0',
        `width:${width}px`,
        'overflow:visible',
        'pointer-events:none',
        'z-index:-1',
    ].join(';');

    clone.style.transform = 'none';
    clone.style.transformOrigin = 'top left';
    clone.style.overflow = 'visible';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    return { clone, wrapper, width, height };
}

export async function generateFuelCardPdfBlob(element) {
    await ensureKalpurushFontLoaded();
    const { clone, wrapper, width } = cloneForExport(element);
    const height = Math.max(clone.scrollHeight, element.scrollHeight) + 8;

    try {
        await document.fonts.ready;
        const canvas = await html2canvas(clone, {
            scale: EXPORT_SCALE,
            width,
            height,
            windowWidth: width,
            windowHeight: height,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#ffffff',
            onclone: prepareFuelCardClone,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        let drawWidth = pdfWidth;
        let drawHeight = imgHeight;

        if (imgHeight > pdfHeight) {
            const fit = pdfHeight / imgHeight;
            drawWidth = pdfWidth * fit;
            drawHeight = pdfHeight;
        }

        const x = (pdfWidth - drawWidth) / 2;
        pdf.addImage(imgData, 'JPEG', x, 0, drawWidth, drawHeight, undefined, 'FAST');

        return pdf.output('blob');
    } finally {
        wrapper.remove();
    }
}

export async function downloadFuelCardPdf(element, filename = 'fuel-card-application.pdf') {
    const blob = await generateFuelCardPdfBlob(element);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Print the generated PDF from the current page. `printBlob` renders the pages
 * into a hidden same-origin iframe and prints that, so the dialog opens right
 * here instead of in a second tab (a pop-up tab is only the fallback if
 * rendering fails).
 * @param {HTMLElement} element
 * @param {string} popupBlockedMessage
 */
export async function printFuelCardForm(element, popupBlockedMessage) {
    const blob = await generateFuelCardPdfBlob(element);
    const opened = await printBlob(blob);
    if (!opened) throw new Error(popupBlockedMessage);
}
